import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { toast } from "sonner"
import {
  getEventQr,
  logEventQrScan,
  submitEventQrFeedback,
  getEventQrCodes,
  getEventQrCode,
  getEventQrCodeSummary,
  createEventQrCode,
  updateEventQrCode,
  setEventQrCodeActive,
  getEventVideos,
  getEventVideoLinkStatus,
  createEventVideo,
  updateEventVideo,
} from "../api/eventQr"

const EVENT_QR_CODES_KEY = ["event-qr-codes"]
const EVENT_VIDEOS_KEY = ["event-videos"]

const errorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback

/* Public event page */

export const useEventQr = (publicUuid) =>
  useQuery({
    queryKey: ["event-qr", publicUuid],
    queryFn: () => getEventQr(publicUuid),
    retry: false,
    enabled: !!publicUuid,
    select: (data) => data?.data,
  })

// Tracking must never interrupt an attendee: failures are swallowed.
export const useLogEventQrScan = () =>
  useMutation({
    mutationFn: logEventQrScan,
    retry: false,
    onError: (error) => {
      console.warn("Failed to log event QR scan:", error)
    },
  })

export const useSubmitEventQrFeedback = () =>
  useMutation({ mutationFn: submitEventQrFeedback })

/* Admin */

// Keeps the current page on screen while the next one loads, so paging
// doesn't flash an empty table.
export const useEventQrCodes = ({ page, perPage, search }) =>
  useQuery({
    queryKey: [...EVENT_QR_CODES_KEY, "list", { page, perPage, search }],
    queryFn: () => getEventQrCodes({ page, perPage, search }),
    placeholderData: keepPreviousData,
  })

export const useEventQrCode = (id) =>
  useQuery({
    queryKey: [...EVENT_QR_CODES_KEY, id],
    queryFn: () => getEventQrCode(id),
    enabled: !!id,
    select: (data) => data?.data,
  })

export const useEventQrCodeSummary = (id) =>
  useQuery({
    queryKey: [...EVENT_QR_CODES_KEY, id, "summary"],
    queryFn: () => getEventQrCodeSummary(id),
    enabled: !!id,
    select: (data) => data?.data,
  })

const useEventQrCodeMutation = (mutationFn, fallbackError) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (response) => {
      // Video counts in the library move with every event change.
      queryClient.invalidateQueries({ queryKey: EVENT_QR_CODES_KEY })
      queryClient.invalidateQueries({ queryKey: EVENT_VIDEOS_KEY })
      toast.success(response?.message || "Saved")
    },
    onError: (error) => toast.error(errorMessage(error, fallbackError)),
  })
}

export const useCreateEventQrCode = () =>
  useEventQrCodeMutation(createEventQrCode, "Failed to create event QR code")

export const useUpdateEventQrCode = () =>
  useEventQrCodeMutation(updateEventQrCode, "Failed to update event QR code")

export const useSetEventQrCodeActive = () =>
  useEventQrCodeMutation(setEventQrCodeActive, "Failed to update event QR code")

export const useEventVideos = () =>
  useQuery({
    queryKey: EVENT_VIDEOS_KEY,
    queryFn: getEventVideos,
    select: (data) => data?.data || [],
  })

// Only fetched once a preview actually opens. A link rarely changes, so a
// result is reused for 5 minutes; editing the video refetches it (its key
// sits under EVENT_VIDEOS_KEY).
export const useEventVideoLinkStatus = (videoId, { enabled }) =>
  useQuery({
    queryKey: [...EVENT_VIDEOS_KEY, videoId, "link-status"],
    queryFn: () => getEventVideoLinkStatus(videoId),
    enabled: enabled && !!videoId,
    staleTime: 5 * 60 * 1000,
    retry: false,
    select: (data) => data?.data,
  })

const useEventVideoMutation = (mutationFn, fallbackError) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: (response) => {
      // A library edit rewrites the title/link on every event using it.
      queryClient.invalidateQueries({ queryKey: EVENT_VIDEOS_KEY })
      queryClient.invalidateQueries({ queryKey: EVENT_QR_CODES_KEY })
      toast.success(response?.message || "Saved")
    },
    onError: (error) => toast.error(errorMessage(error, fallbackError)),
  })
}

export const useCreateEventVideo = () =>
  useEventVideoMutation(createEventVideo, "Failed to add video")

export const useUpdateEventVideo = () =>
  useEventVideoMutation(updateEventVideo, "Failed to update video")
